import React, { useState, useEffect, useCallback } from "react";
import { Spinner } from "react-bootstrap";
import {
  Megaphone, Webhook, Copy, RefreshCw, Save, Eye, EyeOff,
  CheckCircle2, XCircle, AlertTriangle, RotateCcw, Info, Shield,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import CommonToaster from "../Common/CommonToaster";
import {
  AppCard, AppCardContent, AppCardHeader, AppCardTitle,
  AppButton, ConfirmModal, FilterSelect,
} from "../ui";
import { useBranch } from "../../contexts/BranchContext";
import "./settings.scss";

/**
 * Lead Sources Settings — Google Ads (and future Meta/LinkedIn) webhook configuration.
 * Each institute gets its own tenant-scoped webhook URL + signing secret.
 */
const LeadSourcesSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(""); // shown once after rotate
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });

  const { branches } = useBranch();
  const [admins, setAdmins] = useState([]);

  // Google Ads config from server
  const [config, setConfig] = useState({
    enabled: false,
    webhookUrl: "",
    tenantToken: "",
    googleKey: "",
    secretSet: false,
    defaultBranchId: "",
    assignment: { strategy: "round_robin", fixedAssigneeId: "" },
    lastReceivedAt: null,
    totalReceived: 0,
  });

  // Logs
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsFilter, setLogsFilter] = useState("");

  const showToast = (message, type = "success") =>
    setToast({ show: true, message, type });

  // ── Load config ──────────────────────────────────────────────────────────
  const loadConfig = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/lead-sources/google-ads");
      setConfig({
        enabled: !!data.enabled,
        webhookUrl: data.webhookUrl || "",
        tenantToken: data.tenantToken || "",
        googleKey: data.googleKey || "",
        secretSet: !!data.secretSet,
        defaultBranchId: data.defaultBranchId || "",
        assignment: {
          strategy: data?.assignment?.strategy || "round_robin",
          fixedAssigneeId: data?.assignment?.fixedAssigneeId || "",
        },
        lastReceivedAt: data.lastReceivedAt || null,
        totalReceived: data.totalReceived || 0,
      });
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to load configuration", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Load admins for assignee dropdown ────────────────────────────────────
  const loadAdmins = useCallback(async () => {
    try {
      const { data } = await apiClient.get("/admins/list");
      setAdmins(data.admins || data || []);
    } catch {
      /* silent — non-critical */
    }
  }, []);

  // ── Load ingestion logs ──────────────────────────────────────────────────
  const loadLogs = useCallback(async (page = 1, status = "") => {
    setLogsLoading(true);
    try {
      const params = { source: "google_ads", page, limit: 20 };
      if (status) params.status = status;
      const { data } = await apiClient.get("/lead-sources/logs", { params });
      setLogs(data.logs || []);
      setLogsTotalPages(data.totalPages || 1);
      setLogsPage(page);
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to load logs", "error");
    } finally {
      setLogsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConfig();
    loadAdmins();
    loadLogs(1, "");
  }, [loadConfig, loadAdmins, loadLogs]);

  // ── Save config ──────────────────────────────────────────────────────────
  const handleSave = async (overrides = {}) => {
    setSaving(true);
    try {
      const payload = {
        enabled: config.enabled,
        defaultBranchId: config.defaultBranchId || null,
        assignment: {
          strategy: config.assignment.strategy,
          fixedAssigneeId:
            config.assignment.strategy === "fixed"
              ? config.assignment.fixedAssigneeId || null
              : null,
        },
        ...overrides,
      };
      const { data } = await apiClient.patch("/lead-sources/google-ads", payload);
      // If just enabled for the first time, server returns new tokens
      setConfig((c) => ({
        ...c,
        enabled: !!data.enabled,
        webhookUrl: data.webhookUrl || c.webhookUrl,
        tenantToken: data.tenantToken || c.tenantToken,
        googleKey: data.googleKey || c.googleKey,
        secretSet: !!data.secretSet || c.secretSet,
      }));
      // If a secret was generated for the first time, server returns it once
      if (data.secret) {
        setRevealedSecret(data.secret);
        setShowSecret(true);
      }
      showToast("Settings saved", "success");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to save", "error");
    } finally {
      setSaving(false);
    }
  };

  // ── Toggle enable ────────────────────────────────────────────────────────
  const handleToggleEnabled = async () => {
    const next = !config.enabled;
    await handleSave({ enabled: next });
  };

  // ── Rotate secret ────────────────────────────────────────────────────────
  const handleRotateSecret = async () => {
    setShowRotateConfirm(false);
    setRotating(true);
    try {
      const { data } = await apiClient.post("/lead-sources/google-ads/rotate-secret");
      setRevealedSecret(data.secret || "");
      setShowSecret(true);
      setConfig((c) => ({ ...c, secretSet: true }));
      showToast("Secret rotated. Copy it now — it will not be shown again.", "warning");
    } catch (e) {
      showToast(e?.response?.data?.message || "Failed to rotate secret", "error");
    } finally {
      setRotating(false);
    }
  };

  // ── Retry log ────────────────────────────────────────────────────────────
  const handleRetryLog = async (logId) => {
    try {
      await apiClient.post(`/lead-sources/logs/${logId}/retry`);
      showToast("Re-queued for processing", "success");
      loadLogs(logsPage, logsFilter);
    } catch (e) {
      showToast(e?.response?.data?.message || "Retry failed", "error");
    }
  };

  // ── Helpers ──────────────────────────────────────────────────────────────
  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(
      () => showToast(`${label} copied to clipboard`, "success"),
      () => showToast("Copy failed — please copy manually", "error")
    );
  };

  const fmtDate = (d) => (d ? new Date(d).toLocaleString() : "—");

  const statusBadge = (s) => {
    const map = {
      received:  { color: "#0ea5e9", icon: Info,           label: "Received"  },
      processed: { color: "#10b981", icon: CheckCircle2,   label: "Processed" },
      duplicate: { color: "#94a3b8", icon: AlertTriangle,  label: "Duplicate" },
      failed:    { color: "#ef4444", icon: XCircle,        label: "Failed"    },
    };
    const cfg = map[s] || map.received;
    const Icon = cfg.icon;
    return (
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "2px 8px", borderRadius: 12, fontSize: 12,
        background: `${cfg.color}1a`, color: cfg.color,
      }}>
        <Icon size={12} /> {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 40 }}>
        <Spinner animation="border" size="sm" /> &nbsp; Loading…
      </div>
    );
  }

  return (
    <>
      {/* ── Google Ads Card ─────────────────────────────────────────────── */}
      <AppCard>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Megaphone size={18} /> Google Ads — Lead Form Webhook
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">
            Receive leads from Google Ads Lead Form Extension directly into your
            enquiries pipeline. Each institute has a unique webhook URL.
          </p>
        </AppCardHeader>
        <AppCardContent>
          {/* Enable toggle */}
          <div className="settings-form-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <strong>Status:</strong>{" "}
              {config.enabled ? (
                <span style={{ color: "#10b981" }}><CheckCircle2 size={14} /> Enabled</span>
              ) : (
                <span style={{ color: "#94a3b8" }}><XCircle size={14} /> Disabled</span>
              )}
              {config.lastReceivedAt && (
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                  Last lead received: {fmtDate(config.lastReceivedAt)} &middot;
                  &nbsp;Total: {config.totalReceived}
                </div>
              )}
            </div>
            <AppButton onClick={handleToggleEnabled} disabled={saving}>
              {config.enabled ? "Disable" : "Enable"}
            </AppButton>
          </div>

          {/* Webhook URL */}
          {config.enabled && config.webhookUrl && (
            <>
              <div className="settings-field">
                <label>
                  <Webhook size={14} /> Webhook URL
                  <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
                    (paste this into Google Ads → Linked accounts → Webhooks for leads)
                  </span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    readOnly
                    value={config.webhookUrl}
                    style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                  />
                  <AppButton variant="secondary" onClick={() => copyToClipboard(config.webhookUrl, "Webhook URL")}>
                    <Copy size={14} /> Copy
                  </AppButton>
                </div>
              </div>

              {/* Google Key */}
              <div className="settings-field" style={{ marginTop: 12 }}>
                <label>
                  <Shield size={14} /> Key (for Google Ads "Key" field)
                  <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
                    Google Ads echoes this back in the payload to verify the receiver
                  </span>
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text"
                    readOnly
                    value={config.googleKey}
                    style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                  />
                  <AppButton variant="secondary" onClick={() => copyToClipboard(config.googleKey, "Key")}>
                    <Copy size={14} /> Copy
                  </AppButton>
                </div>
              </div>

              {/* Secret */}
              <div className="settings-field" style={{ marginTop: 12 }}>
                <label>
                  <Shield size={14} /> HMAC Signing Secret
                  <span style={{ color: "#64748b", fontSize: 12, marginLeft: 8 }}>
                    Used by signing relays (optional). Header: <code>x-lms-signature: sha256=&lt;hex&gt;</code>
                  </span>
                </label>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type={showSecret ? "text" : "password"}
                    readOnly
                    value={revealedSecret || (config.secretSet ? "••••••••••••••••••••••••" : "")}
                    placeholder={config.secretSet ? "" : "Not generated"}
                    style={{ flex: 1, fontFamily: "monospace", fontSize: 13 }}
                  />
                  {revealedSecret && (
                    <AppButton variant="secondary" onClick={() => setShowSecret((v) => !v)}>
                      {showSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                    </AppButton>
                  )}
                  {revealedSecret && (
                    <AppButton variant="secondary" onClick={() => copyToClipboard(revealedSecret, "Secret")}>
                      <Copy size={14} /> Copy
                    </AppButton>
                  )}
                  <AppButton variant="secondary" onClick={() => setShowRotateConfirm(true)} disabled={rotating}>
                    <RefreshCw size={14} /> Rotate
                  </AppButton>
                </div>
                {revealedSecret && (
                  <div style={{ marginTop: 6, fontSize: 12, color: "#ef4444" }}>
                    <AlertTriangle size={12} /> Copy this secret now — it will not be shown again after you leave this page.
                  </div>
                )}
              </div>

              {/* Default Branch */}
              <div className="settings-field" style={{ marginTop: 16 }}>
                <label>Default Branch (optional)</label>
                <select
                  value={config.defaultBranchId || ""}
                  onChange={(e) => setConfig((c) => ({ ...c, defaultBranchId: e.target.value }))}
                  style={{ width: "100%" }}
                >
                  <option value="">— Use first available —</option>
                  {(branches || []).map((b) => (
                    <option key={b._id} value={b._id}>{b.name}</option>
                  ))}
                </select>
              </div>

              {/* Assignment strategy */}
              <div className="settings-field" style={{ marginTop: 12 }}>
                <label>Auto-Assignment Strategy</label>
                <select
                  value={config.assignment.strategy}
                  onChange={(e) =>
                    setConfig((c) => ({
                      ...c,
                      assignment: { ...c.assignment, strategy: e.target.value },
                    }))
                  }
                  style={{ width: "100%" }}
                >
                  <option value="round_robin">Round-robin among admins</option>
                  <option value="fixed">Fixed assignee</option>
                  <option value="none">No auto-assignment</option>
                </select>
              </div>

              {config.assignment.strategy === "fixed" && (
                <div className="settings-field" style={{ marginTop: 12 }}>
                  <label>Fixed Assignee</label>
                  <select
                    value={config.assignment.fixedAssigneeId || ""}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        assignment: { ...c.assignment, fixedAssigneeId: e.target.value },
                      }))
                    }
                    style={{ width: "100%" }}
                  >
                    <option value="">— Select admin —</option>
                    {admins.map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.fullName || a.name || a.email}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="settings-save-row" style={{ marginTop: 16 }}>
                <AppButton onClick={() => handleSave()} disabled={saving}>
                  <Save size={16} /> {saving ? "Saving…" : "Save Changes"}
                </AppButton>
              </div>
            </>
          )}

          {!config.enabled && (
            <div style={{
              background: "#f1f5f9", padding: 16, borderRadius: 8,
              fontSize: 13, color: "#475569", marginTop: 12,
            }}>
              <Info size={14} /> &nbsp;
              Enable Google Ads above to generate your unique webhook URL and signing key.
              You'll then paste these into your Google Ads account.
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Setup Instructions ──────────────────────────────────────────── */}
      {config.enabled && (
        <AppCard style={{ marginTop: 16 }}>
          <AppCardHeader>
            <AppCardTitle>
              <span className="settings-card-title">
                <Info size={18} /> Setup Instructions
              </span>
            </AppCardTitle>
          </AppCardHeader>
          <AppCardContent>
            <ol style={{ paddingLeft: 20, lineHeight: 1.9, fontSize: 14 }}>
              <li>In Google Ads, open <strong>Tools &amp; Settings → Linked accounts → Webhooks for leads</strong>.</li>
              <li>Paste the <strong>Webhook URL</strong> above.</li>
              <li>Paste the <strong>Key</strong> above into Google's "Key" field.</li>
              <li>Click <strong>Send test data</strong> — a test entry should appear in the Ingestion Logs below.</li>
              <li>Link the webhook to your Lead Form extensions / assets.</li>
            </ol>
          </AppCardContent>
        </AppCard>
      )}

      {/* ── Ingestion Logs ──────────────────────────────────────────────── */}
      <AppCard style={{ marginTop: 16 }}>
        <AppCardHeader>
          <AppCardTitle>
            <span className="settings-card-title">
              <Webhook size={18} /> Ingestion Logs
            </span>
          </AppCardTitle>
          <p className="settings-card-desc">Recent webhook deliveries from all configured lead sources</p>
        </AppCardHeader>
        <AppCardContent>
          <div className="lead-log-filter-row">
            <FilterSelect
              value={logsFilter}
              onChange={(val) => { setLogsFilter(val); loadLogs(1, val); }}
              options={[
                { value: "", label: "All statuses" },
                { value: "received", label: "Received" },
                { value: "processed", label: "Processed" },
                { value: "duplicate", label: "Duplicate" },
                { value: "failed", label: "Failed" },
              ]}
            />
            <AppButton variant="secondary" onClick={() => loadLogs(logsPage, logsFilter)} disabled={logsLoading}>
              <RefreshCw size={14} /> Refresh
            </AppButton>
          </div>

          {logsLoading ? (
            <div style={{ textAlign: "center", padding: 20 }}><Spinner animation="border" size="sm" /></div>
          ) : logs.length === 0 ? (
            <div style={{ textAlign: "center", padding: 20, color: "#94a3b8", fontSize: 14 }}>
              No ingestion logs yet
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table className="lead-log-table">
                <thead>
                  <tr>
                    <th>Received</th>
                    <th>Source</th>
                    <th>Status</th>
                    <th>Signature</th>
                    <th>External ID</th>
                    <th>Enquiry</th>
                    <th>Error</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log._id}>
                      <td style={{ whiteSpace: "nowrap" }}>{fmtDate(log.receivedAt)}</td>
                      <td>{log.source}</td>
                      <td>{statusBadge(log.status)}</td>
                      <td>
                        {log.signatureValid ? (
                          <CheckCircle2 size={14} color="var(--color-success, #10b981)" />
                        ) : (
                          <XCircle size={14} color="var(--color-danger, #ef4444)" />
                        )}
                      </td>
                      <td style={{ fontFamily: "monospace", fontSize: 11 }}>
                        {log.externalLeadId || "—"}
                      </td>
                      <td>
                        {log.enquiryId ? (
                          <a href={`/enquiries/${log.enquiryId._id || log.enquiryId}`} className="lead-log-link">
                            View →
                          </a>
                        ) : "—"}
                      </td>
                      <td className="lead-log-error">
                        {log.errorMessage || "—"}
                      </td>
                      <td>
                        {log.status === "failed" && (
                          <AppButton variant="secondary" size="sm" onClick={() => handleRetryLog(log._id)}>
                            <RotateCcw size={12} /> Retry
                          </AppButton>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {logsTotalPages > 1 && (
            <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 12 }}>
              <AppButton variant="secondary" size="sm"
                disabled={logsPage <= 1}
                onClick={() => loadLogs(logsPage - 1, logsFilter)}>
                Prev
              </AppButton>
              <span style={{ alignSelf: "center", fontSize: 13 }}>
                Page {logsPage} / {logsTotalPages}
              </span>
              <AppButton variant="secondary" size="sm"
                disabled={logsPage >= logsTotalPages}
                onClick={() => loadLogs(logsPage + 1, logsFilter)}>
                Next
              </AppButton>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {/* ── Rotate Secret Confirm ───────────────────────────────────────── */}
      <ConfirmModal
        show={showRotateConfirm}
        title="Rotate Signing Secret?"
        message="Any signing relay using the current secret will stop working until updated with the new value. The new secret is shown only once."
        confirmText="Rotate"
        confirmVariant="danger"
        onCancel={() => setShowRotateConfirm(false)}
        onConfirm={handleRotateSecret}
      />

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </>
  );
};

export default LeadSourcesSettings;
